import React, { useCallback } from 'react';

import ym from 'react-yandex-metrika';

import { generatePath, useHistory, useLocation, useParams } from 'react-router';
import { useMedia } from 'react-use';
import b from 'b_';
import { useRootThemes } from 'platform-components/src/queries/use-root-themes';
import { DEFAULT_GRADE_INFORMATICS } from 'common.components/user/user.constants';
import {
    Link,
    MarkdownExtXContent,
    Select,
    SELECT_THEMES,
    SelectEvent,
    Text,
    useStaticUrls,
} from 'platform-components';
import { useTeacherMainPageCourseContainersQuery } from 'common.components/teacher-main-page/queries';
import { useGradeFromQueryOrTeam } from '../../../../utils/use-current-grade';
import { MEDIA_QUERY_MOBILE } from '../../../library.course-container/constants';

import '../library__informatics.scss';

export const Library__informatics__sidebar = () => {
    const { classId, subject, containerSlug } = useParams<{
        classId?: string;
        subject: string;
        containerSlug?: string;
    }>();
    const rootThemes = useRootThemes({ subjectSlug: subject });
    const firstThemeId = rootThemes.data?.[0].id;

    const staticUrls = useStaticUrls();
    const history = useHistory();
    const { search } = useLocation();
    const grade = useGradeFromQueryOrTeam(DEFAULT_GRADE_INFORMATICS);
    const courseContainers = useTeacherMainPageCourseContainersQuery({
        grade: Number(grade),
    });

    const materialsUrl =
        firstThemeId &&
        generatePath(classId ? staticUrls.labLibrarySubjectTheme : staticUrls.deepLabTheme, {
            classId,
            subject,
            themeId: firstThemeId,
        }) + search;

    const generatePathToCC = useCallback(
        (slug: string) => {
            return generatePath(
                classId ? staticUrls.labLibrarySubjectCollection : staticUrls.labLibrarySubjectCommonCourseContainer,

                {
                    classId,
                    subject,
                    containerSlug: slug,
                }
            );
        },
        [classId, staticUrls, subject]
    );

    const sendMetric = useCallback(() => {
        ym('reachGoal', 'teacher_select_coursecontainer', { from: 'left_menu', grade });
    }, [grade]);

    const onCourseSelect = useCallback(
        (e: SelectEvent<string | null>) => {
            sendMetric();
            const slug = e.target.value;
            if (slug) {
                history.push(`${generatePathToCC(slug)}${search}`);
            }
        },
        [generatePathToCC, history, search, sendMetric]
    );

    const isMobile = useMedia(MEDIA_QUERY_MOBILE);

    if (isMobile) {
        return (
            <div className="sidebar">
                <Select
                    mix="sidebar-themes__select"
                    onSelect={onCourseSelect}
                    placeholder="Все курсы"
                    theme={SELECT_THEMES.ROUNDED}
                    value={containerSlug || null}
                    width="266"
                >
                    {courseContainers.data?.course_container_info.map((courseContainer) => (
                        <Select.Option key={courseContainer.id} value={courseContainer.slug}>
                            <Text ellipsis type={Text.TYPE.MARKDOWN}>
                                {courseContainer.name}
                            </Text>
                        </Select.Option>
                    ))}
                </Select>
            </div>
        );
    }

    return (
        <div className="sidebar">
            {courseContainers.data?.course_container_info.map((courseContainer, i) => {
                const url = `${generatePathToCC(courseContainer.slug)}${search}`;

                return (
                    <SidebarLink
                        key={courseContainer.id || i}
                        name={courseContainer.name}
                        onClick={sendMetric}
                        selected={containerSlug === courseContainer.slug}
                        to={url}
                    />
                );
            })}

            <div className="library__informatics__sidebar-spacer" />

            {materialsUrl && <SidebarLink name="Дополнительные материалы" to={materialsUrl} />}
        </div>
    );
};

type SidebarLinkProps = {
    /**
     * Текст ссылки
     */
    name: string;
    /**
     * URL ссылки
     */
    to: string;
    /**
     * Активна ли ссылка
     */
    selected?: boolean;
    onClick?: () => void;
};

const SidebarLink = (props: SidebarLinkProps) => {
    const { selected, name, to, onClick } = props;

    return (
        <div className="sidebar-themes__root-theme">
            <Link
                isRouter
                mix={b('sidebar-themes', 'root-theme-link', { selected })}
                onClick={onClick}
                theme={Link.THEME.STEALTHY}
                to={to}
            >
                <Text size={Text.SIZE.XL} weight={selected ? Text.WEIGHT.M : Text.WEIGHT.S}>
                    <MarkdownExtXContent>{name}</MarkdownExtXContent>
                </Text>
            </Link>
        </div>
    );
};
